import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { formValueSelector } from 'redux-form';

import { withTheme } from 'common/styling/theme';

import FormFieldsComposer from '../FormFieldsComposer';

import { getDynamicStyles, staticStyles } from './style';

const ObjectField = ({ objectTitle, title, name, items, formName, hidden, submitting, theme, isAllFieldsRequired }) => {
  // 使用 useMemo 稳定 formSelector，避免每次渲染都创建新的 selector
  const formSelector = useMemo(() => formValueSelector(formName), [formName]);

  // 获取program字段的值（用于控制base_for_*字段的显示）
  // 直接获取 type 字段以避免对象引用导致的无限循环
  const programValueRaw = useSelector(state => {
    try {
      // 检查state是否存在
      if (!state) {
        return null;
      }

      // 检查formName是否存在
      if (!formName) {
        return null;
      }

      // 首先尝试直接获取 type 字段
      const typeValue = formSelector(state, 'basic.program.type');
      if (typeValue) {
        return typeValue;
      }

      // 如果 type 字段不存在，尝试从对象中提取
      const value = formSelector(state, 'basic.program');
      if (value && typeof value === 'object' && value.type) {
        return value.type;
      }

      // 如果是字符串，直接返回
      return value;
    } catch (error) {
      // 移除 console.warn 以避免控制台持续输出
      return null;
    }
  });

  // 使用 useMemo 缓存 programValue，确保字符串值稳定
  const programValue = useMemo(() => {
    // 确保返回的是字符串或 null，而不是对象
    if (programValueRaw && typeof programValueRaw === 'object' && programValueRaw.type) {
      return programValueRaw.type;
    }
    return programValueRaw;
  }, [programValueRaw]);

  // 解析当前对象的基础路径，用于查找同层级的 is_set_* 开关
  const basePath = (() => {
    try {
      if (typeof name !== 'string') return '';
      // 针对 condition 数组项，如 "...value.condition[0]"，提取到 "...value"
      const conditionMatch = name.match(/^(.*)\.condition\[\d+\]$/);
      if (conditionMatch) return conditionMatch[1];
      // 其他情况直接使用当前 name 作为基础路径
      return name;
    } catch (e) {
      return name;
    }
  })();

  // 读取同层级的 is_set_* 开关值
  const isSetLot = useSelector(state => {
    try {
      return formSelector(state, `${basePath}.is_set_lot`);
    } catch (e) {
      return undefined;
    }
  });
  const isSetGrossDeposit = useSelector(state => {
    try {
      return formSelector(state, `${basePath}.is_set_gross_deposit`);
    } catch (e) {
      return undefined;
    }
  });
  const isSetNetDeposit = useSelector(state => {
    try {
      return formSelector(state, `${basePath}.is_set_net_deposit`);
    } catch (e) {
      return undefined;
    }
  });

  const partnerMasterIbEnabled = useSelector(state => {
    try {
      return formSelector(state, `${basePath}.partnerMasterIbEnabled`);
    } catch (e) {
      return undefined;
    }
  });

  // // 检查当前ObjectField是否应该显示
  // const shouldShowObject = () => {
  //   try {
  //     // 如果有hidden属性且为true，则隐藏整个ObjectField
  //     if (hidden === true) {
  //       return false;
  //     }
  //
  //     // // 特殊处理：当ObjectField的name是'regular_account'时，依赖于account_type.regular_account
  //     // if (name === 'regular_account') {
  //     //   // 只有当account_type.regular_account为true时才显示regular_account ObjectField
  //     //   return accountTypeRegularAccount === true;
  //     // }
  //     // if (name === 'special_account') {
  //     //   // 只有当account_type.special_account为true时才显示special_account ObjectField
  //     //   return accountTypeSpecialAccount === true;
  //     // }
  //
  //     return true;
  //   } catch (error) {
  //     console.warn('ObjectField: Error in shouldShowObject:', error);
  //     // 出错时默认显示，避免意外隐藏重要字段
  //     return true;
  //   }
  // };

  // 检查字段是否应该显示的函数
  const shouldShowField = (fieldKey, fieldSchema) => {
    try {
      // 检查参数是否有效
      if (!fieldKey || typeof fieldKey !== 'string') {
        return false;
      }

      // 检查fieldSchema是否存在
      if (!fieldSchema || typeof fieldSchema !== 'object') {
        return false;
      }

      // The is_show check has been moved to the render loop to use display: none
      // so that Redux Form does not unmount the field and destroy its value.
      // if (fieldSchema.is_show === false) {
      //   return false;
      // }

      // 根据 is_set_* 开关控制 lot/gross_deposit/net_deposit 的展示
      if (fieldKey === 'lot') {
        return isSetLot === true;
      }
      if (fieldKey === 'gross_deposit') {
        return isSetGrossDeposit === true;
      }
      if (fieldKey === 'net_deposit') {
        return isSetNetDeposit === true;
      }

      if (fieldKey === 'master_table') {
        return partnerMasterIbEnabled === true;
      }

      // 特殊处理：当字段是base_for_volume、base_for_spread或base_for_commissions时，检查program字段
      // const volumeRelatedFields = [
      //   // 'base_for_volume',
      //   // 'base_for_spread',
      //   // 'base_for_commissions',
      //   'group_exceptions',
      //   'symbol_exceptions',
      // ];
      // if (volumeRelatedFields.includes(fieldKey)) {
      //   // 只有当program为traded_volume或trade_volume时才显示这些字段
      //   return ['Traded Volume'].includes(programValue) || ['traded_volume'].includes(programValue);
      // }
      // const pnlRelatedFields = ['profit', 'commission', 'swap', 'pl_percentage'];
      // if (pnlRelatedFields.includes(fieldKey)) {
      //   // 只有当program为traded_volume或trade_volume时才显示这些字段
      //   return ['Profit & Loss'].includes(programValue) || ['pnl'].includes(programValue);
      // }

      return true;
    } catch (error) {
      // 移除 console.warn 以避免控制台持续输出导致页面卡顿
      // 出错时默认显示，避免意外隐藏重要字段
      return true;
    }
  };

  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;

  // // 检查整个ObjectField是否应该显示
  // if (!shouldShowObject()) {
  //   return null;
  // }

  const isAllFieldsHidden = useMemo(() => {
    if (!items || Object.keys(items).length === 0) return false;
    return Object.values(items).every(item => item && item.is_show === false);
  }, [items]);

  return (
    <div
      className={classNames('MultiObjectField', { ObjectField__hidden: hidden })}
      data-field-name={name}
      style={{ display: isAllFieldsHidden ? 'none' : undefined }}>
      {!!objectTitle && <h2>{objectTitle}</h2>}
      {!!title && <FormattedMessage id={title}>{txt => <h3>{txt}</h3>}</FormattedMessage>}
      <div className="ObjectField_items" data-field-name={name}>
        {Object.keys(items).map(key => {
          // 检查字段是否应该显示
          if (!shouldShowField(key, items[key])) return null;

          // 动态修改 schema：当 program 为 trade_volume 或 Traded Volume 时，base_for_volume 为必填
          // 只在必要时创建新对象，避免不必要的重新渲染
          const baseSchema = items[key];
          let fieldSchema = baseSchema;
          if (key === 'base_for_volume') {
            const isTradeVolume = programValue === 'traded_volume';
            // 只在 required 状态真正改变时才创建新对象
            const shouldBeRequired = isTradeVolume ? true : baseSchema.required;
            if (baseSchema.required !== shouldBeRequired) {
              fieldSchema = {
                ...baseSchema,
                required: shouldBeRequired,
              };
            }
          }

          if (key === 'master_table') {
            const shouldBeRequired = partnerMasterIbEnabled === true ? true : baseSchema.required;
            if (baseSchema.required !== shouldBeRequired) {
              fieldSchema = {
                ...baseSchema,
                required: shouldBeRequired,
              };
            }
          }

          return (
            <div key={key} style={{ display: fieldSchema.is_show === false ? 'none' : 'contents' }}>
              <FormFieldsComposer
                name={`${name}.${key}`}
                schema={fieldSchema}
                formName={formName}
                submitting={submitting}
                isAllFieldsRequired={isAllFieldsRequired}
              />
            </div>
          );
        })}
      </div>
      <style jsx>{staticStyles}</style>
      <style jsx>{dynamicStyles}</style>
    </div>
  );
};

ObjectField.propTypes = {
  objectTitle: PropTypes.string,
  title: PropTypes.string,
  name: PropTypes.string.isRequired,
  items: PropTypes.object.isRequired,
  formName: PropTypes.string.isRequired,
  hidden: PropTypes.bool,
  submitting: PropTypes.bool,
  isAllFieldsRequired: PropTypes.bool,
  theme: PropTypes.object,
};

ObjectField.defaultProps = {
  objectTitle: '',
  title: '',
  hidden: false,
  submitting: false,
  isAllFieldsRequired: true,
  theme: {},
};

export default withTheme()(ObjectField);
export { ObjectField };
